import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchFormPackageV2Payers, type FormPayer, type IntakeFormField } from '../../api/bookingApi';

// The insurance-company field on v2 forms (booking-frontend#46, backend#3196): a
// combobox over the payer search. Picking stores the payer's display name as the
// answer and its stedi_id as the companion (the renderer owns both); typing
// without picking keeps the plain text. Any search failure, a 429/401 or the
// server's `fallback_to_free_text` turns every payer field into a plain text
// input for the rest of the session, silently. The query goes only in the POST
// body; nothing here logs or stores it.
//
// Confirming the insurer (booking-frontend#51): text that is not a pick carries
// no payer ID. The renderer looks at such text (a card autofill at once, anything
// else at Next or submit, reusing this field's latest results via `onResults`) and
// may pass a `confirm`: the matches to choose from, with "My insurance isn't
// listed" to keep the text.
//
// The list follows the typing: each keystroke narrows the latest answer to the
// payers that still fit the text, and searches go out while the patient types,
// at most one per PAYER_SEARCH_INTERVAL_MS (the first as soon as the text is long
// enough, the final text last). Text already answered (Backspace, a space) reuses
// its answer. The interval keeps a typed name well inside the server's budget of
// 60 searches a minute per form; a 429 ends the picker.

export const PAYER_SEARCH_INTERVAL_MS = 400;
const MIN_QUERY_LENGTH = 3;
const MAX_QUERY_LENGTH = 200;
// The list sits in the page flow (one page scroll, no nested scroll box), so it
// shows the top matches only; typing more narrows them.
const MAX_SHOWN = 8;
// Answers a field keeps for text the patient may type again.
const MAX_REMEMBERED = 50;

/** The search query for a field's text: trimmed and capped, or null when too short to search. */
export function payerQuery(text: string): string | null {
  const query = text.trim().slice(0, MAX_QUERY_LENGTH);
  return query.length < MIN_QUERY_LENGTH ? null : query;
}

const sameName = (a: string, b: string) => a.replace(/\s+/g, '').toLowerCase() === b.replace(/\s+/g, '').toLowerCase();

const compact = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Whether every word of `text` is in `name`, ignoring case, spaces and punctuation. */
export function payerFitsText(name: string, text: string): boolean {
  const target = compact(name);
  return text.split(/\s+/).every((word) => target.includes(compact(word)));
}

/** The one payer named `text`, ignoring case and whitespace; null when none or several are. */
export function exactPayerMatch(payers: FormPayer[], text: string): FormPayer | null {
  const named = payers.filter((p) => sameName(p.display_name, text));
  return named.length === 1 ? named[0] : null;
}

/** Matches the patient is asked to choose from, for the field's current text. */
export interface PayerConfirmView {
  /** `card`: the text came from the card upload's autofill; `typed`: the patient typed it. */
  source: 'card' | 'typed';
  text: string;
  options: FormPayer[];
}

// Matches CustomFormField's v2 text input.
const INPUT_CLASS =
  'w-full rounded-xl border-2 border-gray-100 bg-white px-4 py-3 text-base outline-none transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 placeholder:text-gray-500';

interface PayerTypeaheadProps {
  field: IntakeFormField;
  inputId: string;
  value: unknown;
  /** The answer is a picked payer (its companion is set). */
  picked: boolean;
  clientId: string;
  token: string;
  /** Session-wide: once set, every payer field is a plain text input. */
  fallback: boolean;
  /** The patient typed: the renderer stores the text and clears any companion. */
  onChange: (text: string) => void;
  onPick: (payer: FormPayer) => void;
  onFallback: () => void;
  /** Ask the patient to choose from these matches (or say it is not listed). */
  confirm?: PayerConfirmView | null;
  onNotListed?: () => void;
  /** The latest search answered: `query` matched `payers`. */
  onResults?: (query: string, payers: FormPayer[]) => void;
}

export function PayerTypeahead({
  field, inputId, value, picked, clientId, token, fallback, onChange, onPick, onFallback, confirm, onNotListed, onResults,
}: PayerTypeaheadProps) {
  // The latest answer: the query it was for and the payers it found.
  const [results, setResults] = useState<{ query: string; payers: FormPayer[] } | null>(null);
  const [open, setOpen] = useState(false);
  // The highlighted payer, kept while answers change the list around it.
  const [active, setActive] = useState<FormPayer | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Every search takes a new number. An answer at or below `droppedRef` is
  // ignored: one already shown is newer, or a reset came after it. Only the
  // latest search's failure ends the picker.
  const seqRef = useRef(0);
  const droppedRef = useRef(0);
  // The query of the latest search still awaited, and when the latest search went
  // out (a dropped one counts against the budget too).
  const sentQueryRef = useRef<string | null>(null);
  const sentAtRef = useRef(0);
  // Answers by query, in memory while the field is on screen.
  const answersRef = useRef(new Map<string, FormPayer[]>());
  // Escape closed the list: answers do not reopen it until the patient types.
  const dismissedRef = useRef(false);
  // The text this field last typed: any other value came from outside (a card
  // autofill, a pick), and the list for the typed text no longer applies.
  const typedRef = useRef<unknown>(value);

  const listboxId = `${inputId}-payers`;
  const optionId = (i: number) => `${inputId}-payer-${i}`;
  const fieldText = typeof value === 'string' ? value : '';
  const fieldQuery = payerQuery(fieldText);
  // The answer for this very text is shown as the server ranked it; an answer for
  // earlier text is narrowed to the payers that still fit.
  const current = !!results && results.query === fieldQuery;
  const matches = !results || !fieldQuery ? []
    : current ? results.payers : results.payers.filter((p) => payerFitsText(p.display_name, fieldQuery));
  const shown = matches.slice(0, MAX_SHOWN);
  const activeIndex = active
    ? shown.findIndex((p) => p.stedi_id === active.stedi_id && p.display_name === active.display_name)
    : -1;
  // An ask about the text in the field takes the place of the field's own list: it
  // has the same matches, plus "My insurance isn't listed".
  const asking = !!confirm && confirm.text === value && confirm.options.length > 0 && !fallback && !picked;
  const expanded = open && !fallback && shown.length > 0 && !asking;

  const cancelSearch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    droppedRef.current = seqRef.current;
    sentQueryRef.current = null;
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setActive(null);
  }, []);

  // Drop the list; nothing can be picked from it until a new search answers.
  const clearList = useCallback(() => {
    setResults(null);
    close();
  }, [close]);

  // Another field fell back: drop pending work and the list.
  useEffect(() => {
    if (!fallback) return;
    cancelSearch();
    clearList();
  }, [fallback, cancelSearch, clearList]);
  // Unmounting: a pending search must not answer into a field that is gone.
  useEffect(() => cancelSearch, [cancelSearch]);
  useEffect(() => {
    if (value === typedRef.current) return;
    typedRef.current = value;
    cancelSearch();
    clearList();
  }, [value, cancelSearch, clearList]);

  function remember(query: string, payers: FormPayer[]) {
    const answers = answersRef.current;
    answers.delete(query);
    answers.set(query, payers);
    const oldest = answers.keys().next().value;
    if (answers.size > MAX_REMEMBERED && oldest !== undefined) answers.delete(oldest);
  }

  function search(query: string) {
    const seq = ++seqRef.current;
    sentQueryRef.current = query;
    sentAtRef.current = Date.now();
    searchFormPackageV2Payers(clientId, token, query)
      .then((res) => {
        if (!res.fallback_to_free_text) remember(query, res.payers);
        if (seq <= droppedRef.current) return;
        if (res.fallback_to_free_text) {
          if (seq === seqRef.current) onFallback();
          return;
        }
        droppedRef.current = seq;
        setResults({ query, payers: res.payers });
        onResults?.(query, res.payers);
        // The patient may have moved on while the search ran: open (and announce)
        // the list only under a field that still has focus; a refocus opens it.
        if (!dismissedRef.current && inputRef.current && document.activeElement === inputRef.current) setOpen(true);
      })
      .catch(() => {
        if (seq <= droppedRef.current || seq !== seqRef.current) return;
        onFallback();
      });
  }

  function handleInput(text: string) {
    typedRef.current = text;
    onChange(text);
    if (fallback) return;
    // The highlight was for the old text: Enter must not pick it.
    setActive(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const query = payerQuery(text);
    if (!query) {
      // Too short to search: no list applies, and no answer still out brings one back.
      cancelSearch();
      clearList();
      return;
    }
    dismissedRef.current = false;
    setOpen(true);
    const answered = answersRef.current.get(query);
    if (answered) {
      // A search still out is for other text: its answer must not replace this one.
      cancelSearch();
      setResults({ query, payers: answered });
      onResults?.(query, answered);
      return;
    }
    if (sentQueryRef.current === query) return;
    const wait = sentAtRef.current + PAYER_SEARCH_INTERVAL_MS - Date.now();
    if (wait <= 0) {
      search(query);
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      search(query);
    }, wait);
  }

  function pick(payer: FormPayer) {
    cancelSearch();
    onPick(payer);
    clearList();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (fallback) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (shown.length === 0) return;
      e.preventDefault();
      dismissedRef.current = false;
      setOpen(true);
      const i = activeIndex;
      setActive(shown[e.key === 'ArrowDown'
        ? (i >= shown.length - 1 ? 0 : i + 1)
        : (i <= 0 ? shown.length - 1 : i - 1)]);
    } else if (e.key === 'Enter') {
      if (expanded && activeIndex >= 0) {
        // Keep the form's Enter handling (move to the next field) out of a pick.
        e.preventDefault();
        e.stopPropagation();
        pick(shown[activeIndex]);
      } else {
        close();
      }
    } else if (e.key === 'Escape') {
      if (expanded) {
        e.preventDefault();
        e.stopPropagation();
        dismissedRef.current = true;
        close();
      }
    }
  }

  const combobox: React.InputHTMLAttributes<HTMLInputElement> = fallback ? {} : {
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-expanded': expanded,
    'aria-controls': listboxId,
    'aria-activedescendant': expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined,
  };
  const pickedNoteId = `${inputId}-picked`;
  // Announced once the answer for this very text is in, not at each narrowing.
  const showStatus = !fallback && open && current;
  const noMatches = showStatus && shown.length === 0;
  // An answer removes the ask (and the focused choice): focus goes back to the field.
  const answer = (act: () => void) => {
    act();
    inputRef.current?.focus();
  };
  const confirmTitleId = `${inputId}-confirm-title`;

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className={`${INPUT_CLASS} ${picked ? 'pr-11' : ''}`}
          placeholder={field.placeholder ?? (fallback ? '' : 'Start typing to search')}
          value={fieldText}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={close}
          onFocus={() => { dismissedRef.current = false; if (shown.length > 0) setOpen(true); }}
          autoComplete="off"
          autoCapitalize="words"
          spellCheck={false}
          enterKeyHint="next"
          aria-describedby={picked ? pickedNoteId : undefined}
          {...combobox}
        />
        {picked && (
          <>
            <svg aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <span id={pickedNoteId} className="sr-only">Selected from the list.</span>
          </>
        )}
      </div>
      {!fallback && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Insurance companies"
          hidden={!expanded}
          className="mt-2 rounded-xl border-2 border-gray-100 bg-white overflow-hidden divide-y divide-gray-100"
        >
          {shown.map((payer, i) => (
            <li
              key={`${payer.stedi_id}-${i}`}
              id={optionId(i)}
              role="option"
              aria-selected={i === activeIndex}
              // Keep focus in the input so a click is not lost to its blur.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(payer)}
              className={`flex items-center min-h-[48px] px-4 py-3 text-base cursor-pointer break-words ${
                i === activeIndex ? 'bg-primary/10 text-primary-deep font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {payer.display_name}
            </li>
          ))}
        </ul>
      )}
      {asking && (
        // Focusable (not tabbable) so Next can bring it into view and have it read
        // out, without putting focus on a choice a stray Enter would pick.
        <div
          role="group"
          aria-labelledby={confirmTitleId}
          tabIndex={-1}
          data-payer-confirm
          className="mt-2 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 outline-none focus:ring-2 focus:ring-amber-300"
        >
          <p id={confirmTitleId} className="text-sm text-gray-800 mb-2.5 break-words">
            {confirm.source === 'card'
              ? <>We read <strong className="font-semibold">{confirm.text}</strong> from your card. Choose your insurance company.</>
              : 'Choose your insurance company from the list.'}
          </p>
          <div className="space-y-2">
            {confirm.options.slice(0, MAX_SHOWN).map((payer, i) => (
              <button
                key={`${payer.stedi_id}-${i}`}
                type="button"
                onClick={() => answer(() => pick(payer))}
                className="flex items-center w-full min-h-[48px] px-4 py-3 rounded-xl border-2 border-gray-100 bg-white text-left text-base text-gray-800 hover:bg-gray-50 break-words"
              >
                {payer.display_name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => answer(() => { cancelSearch(); clearList(); onNotListed?.(); })}
              className="flex items-center w-full min-h-[48px] px-4 py-3 rounded-xl text-left text-base font-semibold text-primary-deep hover:bg-white"
            >
              My insurance isn't listed
            </button>
          </div>
        </div>
      )}
      <p role="status" className={noMatches ? 'text-xs text-gray-500 mt-1.5' : 'sr-only'}>
        {noMatches
          ? 'No matches. You can keep what you typed.'
          : showStatus
            ? `${shown.length} insurance ${shown.length === 1 ? 'company' : 'companies'} found. Use the arrow keys to choose.`
            : ''}
      </p>
    </div>
  );
}
